import { Injectable } from '@angular/core';
import { HttpRequest } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import {
  buildCanonicalString,
  base64FromArrayBuffer,
  generateNonceHex,
  sha256Hex,
  splitUrl,
  EMPTY_BODY_SHA256_HEX,
} from './signing-canonical';

/** Servlet context path baked into environment.endPoint; stripped to get the app-relative path. */
const CONTEXT_PATH = '/rest/lychee-v2';
const CLIENT_ID = 'lychee-store';

interface SigningConfig {
  keyId: string;
  privateKeyPkcs8Pem: string;
}

/**
 * Signs outgoing HttpRequests with LYCHEE-SIGN-V1 (RSASSA-PKCS1-v1.5 / SHA-256 via Web Crypto).
 * The private key is inlined at build time from environment.signing — it is fully extractable from
 * the JS bundle; this stops scripted abuse + gives revocation, not key-extraction attacks (see plan
 * threat model). If no key is configured, signing is a no-op (lets unconfigured builds run).
 */
@Injectable({ providedIn: 'root' })
export class RequestSigner {

  private keyPromise: Promise<CryptoKey> | null = null;

  private get config(): SigningConfig | null {
    const signing = (environment as any).signing;
    if (!signing || !signing.privateKeyPkcs8Pem || !signing.keyId) {
      return null;
    }
    return signing as SigningConfig;
  }

  /** True when a signing key is configured for this build. */
  get enabled(): boolean {
    return this.config != null;
  }

  /**
   * Computes the LYCHEE-SIGN-V1 headers for `request` (after URL/header mutations, before send).
   * Returns null when signing is disabled or the body is not JSON-hashable (FormData/Blob) — those
   * paths are never protected server-side, so leaving them unsigned is safe.
   */
  async signHeaders(request: HttpRequest<any>): Promise<{ [name: string]: string } | null> {
    const cfg = this.config;
    if (!cfg) {
      return null;
    }
    if (request.body instanceof FormData || request.body instanceof Blob) {
      return null;
    }

    const { path, query } = splitUrl(request.url, CONTEXT_PATH);
    const bodyHashHex = await this.hashBody(request.body);
    const timestampMs = String(Date.now());
    const nonce = generateNonceHex(16);

    const canonical = buildCanonicalString(
      request.method, path, query, 'sha256', bodyHashHex, timestampMs, nonce, CLIENT_ID, cfg.keyId);

    const key = await this.privateKey(cfg);
    const signatureBuffer = await crypto.subtle.sign(
      'RSASSA-PKCS1-v1_5', key, new TextEncoder().encode(canonical));

    return {
      'X-Lychee-Sign-Version': '1',
      'X-Lychee-Client': CLIENT_ID,
      'X-Lychee-Key-Id': cfg.keyId,
      'X-Lychee-Timestamp': timestampMs,
      'X-Lychee-Nonce': nonce,
      'X-Lychee-Body-Mode': 'sha256',
      'X-Lychee-Signature': base64FromArrayBuffer(signatureBuffer),
    };
  }

  /** Hash the EXACT bytes Angular will send (mirror its JSON.stringify of object/array bodies). */
  private async hashBody(body: any): Promise<string> {
    if (body === null || body === undefined || body === '') {
      return EMPTY_BODY_SHA256_HEX;
    }
    const serialized = typeof body === 'string' ? body : JSON.stringify(body);
    return sha256Hex(serialized);
  }

  private privateKey(cfg: SigningConfig): Promise<CryptoKey> {
    if (!this.keyPromise) {
      this.keyPromise = Promise.resolve(crypto.subtle.importKey(
        'pkcs8',
        pemToArrayBuffer(cfg.privateKeyPkcs8Pem),
        { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' },
        false,
        ['sign']));
    }
    return this.keyPromise;
  }
}

/** Decode a PKCS#8 PEM private key to its DER ArrayBuffer. */
function pemToArrayBuffer(pem: string): ArrayBuffer {
  const base64 = pem
    .replace('-----BEGIN PRIVATE KEY-----', '')
    .replace('-----END PRIVATE KEY-----', '')
    .replace(/\s/g, '');
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}
